import { useDispatch, useSelector } from "react-redux";
import * as coreSelectors from "core/selectors";
import * as serviceSelectors from "service/selectors";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DataTable from "ui/DataTable";
import * as MessageFormat from "./MessageFormat";
import {
  Badge,
  Button,
  Grid,
  IconButton,
  styled,
  Tabs,
  Tab,
  Typography,
  useTheme,
} from "@material-ui/core";
import { openSendText, setSmsTask, setTextSearch,
         archiveSmsTask, unarchiveSmsTask } from "./actions";
import AddIcon from "@material-ui/icons/Add";
import ArchiveIcon from "@material-ui/icons/Archive";
import UnarchiveIcon from "@material-ui/icons/Unarchive";
import { ExportIcon, TextMessageIcon } from "./Icons";
import OverlayLoader from "ui/OverlayLoader";
import ActivityDatePicker from "./ActivityDatePicker";
import SearchInput from "ui/SearchInput";
import TextThread from "./TextThread";
import {
  canAccessTexts,
  getDistictFilteredAccounts,
  getPreviousDate,
  searchField,
} from "utils";
import { formatPhoneNumber } from "ui/Formatters";
import RestrictedPage from "ui/RestrictedPage";
import { TextRestrictedRoles } from "constants/account.roles.constants";
import * as ServiceActions from "./actions";
import { postSnackMessage } from "core/actions";

const ComponentWrapper = styled("div")(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "stretch",
  alignItems: "stretch",
}));
const TextContainer = styled("div")(({ theme, isThreadOpen }) => ({
  display: "grid",
  height: "100%",
  width: `calc(100vw - 23px)`,
  position: "relative",
  gridTemplateColumns: "30% 70%",
  gridTemplateRows: "1fr",
  overflow: "hidden",
  marginTop: "10px",
  gridGap: "10px",
  // Let text list take up full screen when no thread is open
  "& :only-child": {
    gridColumn: "1/-1",
    display: isThreadOpen ? "none" : "auto",
  },
  [theme.breakpoints.up("md")]: {
    width: `calc(100vw - ${theme.sizes.drawerWidth} - 23px)`,
  },
}));
const SmsTaskListContainer = styled("div")(({ theme, isThreadOpen }) => ({
  width: "100%",
  hieght: "100%",
  overflowY: "scroll",
  opacity: isThreadOpen ? "0" : "1",
  [theme.breakpoints.up("md")]: {
    width: "100%",
    opacity: 1,
  },
}));

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  flex: "1 1 auto",
  margin: "0 !important",
  "& >*": {
    margin: "10px 3px 0 0",
  },
  flexWrap: "wrap",
  [theme.breakpoints.up("sm")]: {
    flexWrap: "nowrap",
  },
}));

const SearchContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "stretch",
  flexWrap: "wrap",
  justifyContent: "space-between",
  width: "100%",
  [theme.breakpoints.up("md")]: {
    // flexDirection:'row',
    alignItems: "center",
  },
}));
const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    background: theme.brandingColors.rubyRed,
    color: "#fff",
  },
}));
const ExportCSVText = styled(Typography)(({ theme }) => ({
  fontFamily: "Sofia-bold",
  cursor: "pointer",
  color: "#09122B",
  fontWeight: 400,
  fontSize: 14,
  textDecoration: "underline",
  "& :hover": {
    color: "#E5196C",
  },
  "& svg": {
    margin: "0 5px",
  },
  "& span": {
    margin: "0 2px",
  },
  "& span:first-child": {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "nowrap",
  },
}));

function Text() {
  const dataTableRef = useRef(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [startTime, setStartTime] = useState(getPreviousDate(30));
  const [endTime, setEndTime] = useState(new Date());
  const [currentTab, setCurrentTab] = useState(0); 
  const dispatch = useDispatch();
  const theme = useTheme();
  const [canLoadMore, setCanLoadmore] = useState(false);
  const {
    showLoading,
    textSearch,
    smsTasks = [],
    currentTaskInfo,
    user,
  } = useSelector((state) => ({
    showLoading: serviceSelectors.getUpdatingSmsTasks(state),
    textSearch: serviceSelectors.getTextSearch(state),
    smsTasks: serviceSelectors.getLast20Messages(state)?.smsTasks,
    currentTaskInfo: serviceSelectors.getSmsTask(state),
    user: coreSelectors.getUser(state),
  }));
  // Memoize sort function
  const sortSmsTasks = useCallback(
    (smsTasks) => {
      let minDate = new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000);
      let maxDate = new Date();
      if (startTime !== null && startTime !== undefined) {
        minDate = new Date(startTime);
        minDate.setHours(0, 0, 0, 0);
      }
      if (endTime !== null && endTime !== undefined) {
        maxDate = new Date(endTime);
        maxDate.setHours(24, 0, 0, 0);
      }
      smsTasks = smsTasks.filter(
        (task) =>
          new Date(task?.lastMessage).getTime() >= minDate.getTime() &&
          new Date(task?.lastMessage).getTime() <= maxDate.getTime()
      );
      // Sort tasks by date
      return smsTasks.sort(
        (task1, task2) =>
          new Date(task2.lastMessage) - new Date(task1.lastMessage)
      );
    },
    [startTime, endTime]
  );
  // Momoize data processing
  const [displayTasks, selectedTaskInfo] = useMemo(() => {
    const distinctUserAccountIds = getDistictFilteredAccounts(
      user,
      (role) => !TextRestrictedRoles.includes(role.userRoleKey)
    ).map((account) => account.accountId);
    let filteredSmsTasks = smsTasks.filter((smsTask) =>
      distinctUserAccountIds.includes(smsTask.accountId)
    );
    
    if (currentTab === 0) {
      // All tab - show non-archived only
      filteredSmsTasks = filteredSmsTasks.filter((task) => !task.messageArchived);
    } else if (currentTab === 1) {
      // Archive tab - show archived only
      filteredSmsTasks = filteredSmsTasks.filter((task) => task.messageArchived);
    }
    
    if (textSearch) {
      let phoneSearchText = textSearch.replace(/[()-\s]/g, "");
      let searchText = textSearch.toUpperCase();
      let displayTasks = {};
      // Filter by accountName
      filteredSmsTasks.forEach((smsTask) => {
        if (searchField(smsTask.accountName, searchText)) {
          displayTasks[smsTask.taskId] = smsTask;
        }
      });
      // Message level filtering
      filteredSmsTasks.forEach((task) => {
        let messages = task.messages.filter((message) => {
          if (searchField(message.textContent, searchText)) {
            return true;
          }
          if (searchField(message.sendingUser, searchText)) {
            return true;
          }
          if (searchField(message.ani, phoneSearchText)) {
            return true;
          }
          if (searchField(message?.dnis, phoneSearchText)) {
            return true;
          }
          return false;
        });
        if (messages.length > 0) {
          if (displayTasks[task.taskId]) {
            displayTasks[task.taskId] = {
              ...displayTasks[task.taskId],
              messages,
            };
          } else {
            displayTasks[task.taskId] = {
              ...task,
              messages,
            };
          }
        }
      });
      displayTasks = Object.values(displayTasks);
      return [
        sortSmsTasks(displayTasks),
        displayTasks.find((task) => task.taskId === currentTaskInfo.taskId) || {
          messages: [],
        },
      ];
    } else {
      // Return all the smsTasks and currentTaskInfo if there is no text search
      return [
        sortSmsTasks(filteredSmsTasks),
        filteredSmsTasks.find(
          (task) => task.taskId === currentTaskInfo.taskId
        ) || {
          messages: [],
        },
      ];
    }
  }, [textSearch, smsTasks, currentTaskInfo, sortSmsTasks, user, currentTab]);

  const closeChat = () => {
    dataTableRef.current?.resetSelection();
    dispatch(setSmsTask({ messages: [] }));
  };

  const columns = [
    {
      heading: "Contact",
      textAlign: "left",
      renderCell: (smsTask, isRowSelected) => {
        return (
          <>
            <IconButton>
              <StyledBadge
                style={{
                  marginRight: "10px",
                }}
                badgeContent={smsTask?.unread}
              >
                <TextMessageIcon
                  style={{
                    color: isRowSelected
                      ? "#fff"
                      : theme.brandingColors.rubyDarkBlue,
                  }}
                />
              </StyledBadge>
            </IconButton>
            <span
              style={{
                fontSize: 14,
                fontFamily: "sofia",
                color: isRowSelected ? "#fff" : "#000",
              }}
            >
              {/* {smsTask.taskId} */}
              {smsTask.smsDisplayName
                ? smsTask.smsDisplayName
                : `${MessageFormat.messageDetail(smsTask, user)}`}
            </span>
          </>
        );
      },
    },
    {
      heading: "Account (Ruby Number)",
      textAlign: "left",
      renderCell: (smsTask, isRowSelected) => {
        return (
          <span
            style={{
              fontSize: 14,
              fontFamily: "sofia",
              color: isRowSelected ? "#fff" : "#000",
            }}
          >
            {smsTask.accountName} ({formatPhoneNumber(smsTask?.dnis)})
          </span>
        );
      },
    },
    {
      heading: "",
      textAlign: "right",
      renderCell: (smsTask, isRowSelected) => {
        return (
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              const lastMessageId = smsTask.lastMessageId || 
                (smsTask.messages && smsTask.messages.length > 0 
                  ? smsTask.messages[smsTask.messages.length - 1].messageId 
                  : null);
              
              if (!lastMessageId) return;
              if (currentTab === 0) {
                dispatch(archiveSmsTask(smsTask.taskId, lastMessageId));
              } else {
                dispatch(unarchiveSmsTask(smsTask.taskId, lastMessageId));
              }
            }}
          >
            {currentTab === 0 ? (
              <ArchiveIcon
                style={{
                  color: isRowSelected ? "#fff" : "#000",
                }}
              />
            ) : (
              <UnarchiveIcon
                style={{
                  color: isRowSelected ? "#fff" : "#000",
                }}
              />
            )}
          </IconButton>
        );
      },
    },
  ];
  const changeSmsTask = (row) => {
    dispatch(setSmsTask(row));
  };
  const querySmsTasks = useCallback(
    ({
      startDate = undefined,
      endDate = undefined,
      pageNumber = 1,
      success,
    } = {}) => {
      // console.log("Querying with start date", startTime);
      dispatch(
        ServiceActions.querySmsTasks({
          startDate: startDate ?? startTime,
          endDate,
          pageNumber,
          // Forcing this sms version gets all smsTasks in the time range
          smsVersion: "0",
          success: (response) => {
            if (typeof success === "function") {
              success(response);
            }
            if (response?.smsTasks && response?.smsTasks?.length) {
              setCanLoadmore(true);
            } else {
              setCanLoadmore(false);
            }
          },
          fail: () => {
            console.log("the api failed");
          },
        })
      );
    },
    [dispatch, startTime]
  );

  const loadMore = () => {
    querySmsTasks({
      endDate: endTime,
      pageNumber: currentPageNumber + 1,
      success: () => {
        setCurrentPageNumber((state) => state + 1);
      },
    });
  };
  const generateCSVReport = () => {
    let minDate = new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000);
    let maxDate = new Date(new Date());
    if (startTime !== null) {
      minDate = new Date(startTime);
      minDate.setHours(0, 0, 0, 0);
    }
    if (endTime !== null) {
      maxDate = new Date(endTime);
      maxDate.setHours(24, 0, 0, 0);
    }
    if (Math.floor((maxDate - minDate) / (1000 * 60 * 60 * 24)) > 31) {
      dispatch(
        postSnackMessage("error", "", "CSV export can only load max of 31 days")
      );
      return;
    }
    console.log("generate now");
    dispatch(ServiceActions.generateCsvSmsReport(minDate, maxDate));
    // this.props.generateCSV(this.props.filterType, null, minDate, maxDate, (this.props.filterSearch || '').trim().toUpperCase(), (this.state.selectedMessages || []).join(','));
  };

  useEffect(() => {
    document.title = "Ruby - Texts";
    querySmsTasks({
      endDate: endTime,
    });
    return () => {
      // Reset sms task on component unmount
      dispatch(setSmsTask({ messages: [] }));
    };
  }, [dispatch, startTime, endTime, querySmsTasks]);

  // console.log("The tasks are", smsTasks, startTime, endTime, currentPageNumber);

  if (!canAccessTexts(user)) {
    return <RestrictedPage />;
  }
  return (
    <ComponentWrapper>
      {showLoading && <OverlayLoader />}
      <Tabs
        value={currentTab}
        onChange={(event, newValue) => {
          setCurrentTab(newValue);
        }}
        indicatorColor="secondary"
        style={{ marginBottom: '10px' }}
      >
        <Tab label="All" style={{ textTransform: 'none' }} />
        <Tab label="Archive" style={{ textTransform: 'none' }} />
      </Tabs>
      <SearchContainer>
        <Row>
          <SearchInput
            value={textSearch}
            onChange={(e) => dispatch(setTextSearch(e.target.value))}
            onCloseIconClick={() => dispatch(setTextSearch(""))}
          />
          <IconButton onClick={() => dispatch(openSendText(""))}>
            <AddIcon />
          </IconButton>
        </Row>
        <Row>
          <ActivityDatePicker
            startTime={startTime}
            endTime={endTime}
            isActivityPage={false}
            setStartTime={(value) => {
              setCurrentPageNumber(1);
              closeChat();
              setStartTime(value !== null ? value.toJSDate() : null);
            }}
            setEndTime={(value) => {
              setCurrentPageNumber(1);
              closeChat();
              setEndTime(value !== null ? value.toJSDate() : null);
            }}
          />
          <ExportCSVText
            onClick={generateCSVReport}
            // className={csvDisabled ? classes.linkDisabled : classes.link}
          >
            <span>
              <ExportIcon />
              <span style={{ textWrap: "nowrap" }}>Export CSV</span>
            </span>
          </ExportCSVText>
        </Row>
      </SearchContainer>
      <TextContainer>
        <SmsTaskListContainer
          isThreadOpen={
            selectedTaskInfo && !!selectedTaskInfo?.messages?.length
          }
        >
          <DataTable
            enableNoContentPlaceholder
            ref={dataTableRef}
            columns={columns}
            data={displayTasks}
            onRowClick={changeSmsTask}
            getRowId={(row) => row.taskId}
            marginTop={0}
          />
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            style={{ padding: "15px 0" }}
          >
            {!!displayTasks?.length &&
              (canLoadMore ? (
                <Grid item>
                  <Button
                    style={{ alignSelf: "center", justifySelf: "center" }}
                    onClick={loadMore}
                  >
                    Load More
                  </Button>
                </Grid>
              ) : (
                <Grid style={{ padding: "25px 0" }} item>
                  <Typography
                    style={{
                      fontFamily: "Sofia",
                      fontSize: 18,
                      textAlign: "center",
                      padding: "10px",
                    }}
                  >
                    Showing 1 - {smsTasks?.length} Please change the start date
                    to load more
                  </Typography>
                </Grid>
              ))}
          </Grid>
        </SmsTaskListContainer>
        <>
          {selectedTaskInfo && !!selectedTaskInfo?.messages?.length && (
            <TextThread
              onChatClose={() => dataTableRef.current.resetSelection()}
              selectedTaskInfo={selectedTaskInfo}
            />
          )}
        </>
      </TextContainer>
    </ComponentWrapper>
  );
}

export default Text;

